import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { CardData } from '../PricesSection/PricesSection';

interface DynamicSEOProps {
  cards: CardData[];
}

/**
 * Динамический SEO компонент
 * Обновляет meta теги на основе доступных стран из API
 */
const DynamicSEO: React.FC<DynamicSEOProps> = ({ cards }) => {
  const { i18n } = useTranslation();
  const isRu = i18n.language.startsWith('ru');

  useEffect(() => {
    const updateSEO = () => {
      try {
        if (cards.length > 0) {
          const countries = cards.map(card => 
            isRu ? card.name_ru : card.name_en
          );
          
          const countriesString = countries.join(', ');
          
          // Обновляем title
          const titleEn = `ProxyFlash — Mobile 5G Proxies in ${countriesString} | Private & Fast 24/7`;
          const titleRu = `ProxyFlash — Мобильные 5G прокси в ${countriesString} | Приватные и быстрые 24/7`;
          document.title = isRu ? titleRu : titleEn;
          
          // Обновляем description
          const descriptionEn = `Private mobile 5G proxies in ${countriesString}. HTTP, SOCKS5, VPN support. Fast IP rotation every minute. 24/7 management via Telegram bot. Trusted by affiliate marketers and targetologists.`;
          const descriptionRu = `Приватные мобильные 5G прокси в ${countriesString}. Поддержка HTTP, SOCKS5, VPN. Быстрая ротация IP каждую минуту. Управление 24/7 через Telegram бот. Доверяют арбитражники и таргетологи.`;
          
          const metaDescription = document.querySelector('meta[name="description"]');
          if (metaDescription) {
            metaDescription.setAttribute('content', isRu ? descriptionRu : descriptionEn);
          }
          
          // Обновляем keywords с упоминанием стран
          const keywordsEn = `mobile proxy, 5G proxy, private proxy, SOCKS5, HTTP proxy, VPN, IP rotation, ${countries.map(c => `${c} proxy`).join(', ')}, proxy service, buy proxy, ProxyFlash`;
          const keywordsRu = `мобильные прокси, 5G прокси, приватные прокси, SOCKS5, HTTP прокси, VPN, ротация IP, ${countries.map(c => `прокси ${c}`).join(', ')}, прокси сервис, купить прокси, ProxyFlash`;
          
          const metaKeywords = document.querySelector('meta[name="keywords"]');
          if (metaKeywords) {
            metaKeywords.setAttribute('content', isRu ? keywordsRu : keywordsEn);
          }
          
          // Обновляем Open Graph title и description
          const ogTitle = document.querySelector('meta[property="og:title"]');
          if (ogTitle) {
            ogTitle.setAttribute('content', isRu ? titleRu : titleEn);
          }
          
          const ogDescription = document.querySelector('meta[property="og:description"]');
          if (ogDescription) {
            ogDescription.setAttribute('content', isRu ? descriptionRu : descriptionEn);
          }
          
          // Обновляем Twitter Card
          const twitterTitle = document.querySelector('meta[name="twitter:title"]');
          if (twitterTitle) {
            twitterTitle.setAttribute('content', isRu ? titleRu : titleEn);
          }
          
          const twitterDescription = document.querySelector('meta[name="twitter:description"]');
          if (twitterDescription) {
            twitterDescription.setAttribute('content', isRu ? descriptionRu : descriptionEn);
          }
          
          // Обновляем Structured Data для поисковых систем
          updateStructuredData(cards, isRu, countries);
        }
      } catch (error) {
        // silent fail
      }
    };

    updateSEO();
  }, [cards, i18n.language, isRu]);

  return null;
};

/**
 * Обновляет structured data (JSON-LD) с информацией о странах
 */
function updateStructuredData(cards: CardData[], isRu: boolean, countries: string[]): void {
  // Обновляем Service structured data
  const serviceScript = document.querySelector('script[type="application/ld+json"]');
  if (serviceScript) {
    try {
      const structuredData = {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "ProxyFlash Mobile Proxy Service",
        "description": isRu 
          ? `Приватные мобильные 5G прокси с поддержкой HTTP, SOCKS5, VPN. Ротация IP каждую минуту. Управление 24/7 через Telegram бот. Доступны в: ${countries.join(', ')}.`
          : `Private mobile 5G proxies with HTTP, SOCKS5, VPN support. IP rotation every minute. 24/7 management via Telegram bot. Available in: ${countries.join(', ')}.`,
        "provider": {
          "@type": "Organization",
          "name": "ProxyFlash",
          "url": "https://proxyflash.net"
        },
        "serviceType": "Proxy Service",
        "areaServed": cards.map(card => ({
          "@type": "Country",
          "name": isRu ? card.name_ru : card.name_en
        })),
        "availableChannel": {
          "@type": "ServiceChannel",
          "name": "Telegram Bot",
          "url": "https://t.me/proxyflashBot"
        },
        "offers": {
          "@type": "AggregateOffer",
          "priceCurrency": "USD",
          "lowPrice": Math.min(...cards.map(c => parseFloat(c.day_price.replace(/[^0-9.]/g, '')))),
          "highPrice": Math.max(...cards.map(c => parseFloat(c.month_price.replace(/[^0-9.]/g, '')))),
          "offerCount": cards.length
        }
      };
      
      // Создаем новый script элемент
      const newScript = document.createElement('script');
      newScript.type = 'application/ld+json';
      newScript.textContent = JSON.stringify(structuredData, null, 2);
      
      // Находим все script элементы с JSON-LD и обновляем Service
      const allScripts = document.querySelectorAll('script[type="application/ld+json"]');
      allScripts.forEach((script) => {
        try {
          const data = JSON.parse(script.textContent || '');
          if (data['@type'] === 'Service') {
            script.textContent = JSON.stringify(structuredData, null, 2);
          }
        } catch (e) {
          // Skip invalid JSON
        }
      });
    } catch (error) {
      console.error('Failed to update structured data:', error);
    }
  }
}

export default DynamicSEO;
